import { useState } from 'react';
import { Button, Form, Offcanvas} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import keywordsApi, {
  useCreateGroupMutation,
  useGetTariffInfoQuery,
} from '@/app/KeywordTracker/api/keywordsApi';
import { Formik } from 'formik';
import { object, string } from 'yup';
import { errorMessage, successMessage } from '@/utils/toast';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';
import { useDispatch, useSelector } from 'react-redux';
import { addTransaction } from '@/app/common/components/ag-grid/addTransaction';
import { hideModal } from '@/app/common/reducers/simpleModal';
import KeywordsForSelect from './KeywordsForSelect';
import Picker from '@emoji-mart/react';
import useDomain from '@/hooks/useDomain';
import type { GridRef } from './types';

interface AddGroupsDrawerProps {
  gridRef: GridRef;
}

type SimpleModalRootState = {
  simpleModal: string;
};

const AddGroupsDrawer = ({ gridRef }: AddGroupsDrawerProps) => {
  const modalKey = useSelector(
    (state: SimpleModalRootState) => state.simpleModal,
  );
  const { domain_slug } = useDomain();
  const [previewEmoji, setPreviewEmoji] = useState(false);
  const dispatch = useDispatch();
  const [createGroup] = useCreateGroupMutation();
  const { data: trackerInfo, isLoading } =
    useGetTariffInfoQuery(domain_slug);
  const {
    config: { isDark },
  } = useAppContext() as unknown as { config: { isDark: boolean } };


  return (
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={modalKey === 'ADD_GROUP'}
      onHide={() => {
        dispatch(hideModal());
      }}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">افزودن دسته</div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="pb-5 pt-0">
        <Formik
          onSubmit={async ({ title, keyword_ids }, action) => {
            createGroup({
              title: title.trim(),
              keyword_ids: keyword_ids.map(item => item.value.trim()),
              domain_slug
            })
              .unwrap()
              .then(res => {
                if (res?.success) {
                  successMessage('دسته با موفقیت اضافه شد');
                  addTransaction(res?.data, gridRef);
                  dispatch(keywordsApi.util.invalidateTags(['keywordsPage']));
                } else if (res?.resultCode === 50004)
                  errorMessage('دسته ای با این عنوان وجود داره');
              })
              .catch(err => {
                errorMessage('خطایی در ایجاد تگ وجود داره');
                console.error(err);
              });
            dispatch(hideModal());
          }}
          initialValues={{ title: '', keyword_ids: [] }}
          enableReinitialize
          validationSchema={object().shape({
            title: string()
              .trim()
              .required('لطفا نام دسته رو بنویسید')
              .max(50, 'نام دسته نباید بیشتر از ۵۰ کاراکتر باشد.')
          })}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) =>
            !isLoading && (
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <Form.Group className="mb-1 position-relative">
                  <Form.Label>عنوان دسته</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="یک عنوان برای متمایز کردن دسته بنویس"
                    value={values.title}
                    isInvalid={touched.title && errors.title}
                    isValid={touched.title && !errors.title}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    onBlur={handleBlur}
                    name="title"
                  />
                  <Button
                    variant="link"
                    className="emoji-icon position-absolute"
                    style={{
                      top: 33,
                      color: getColor(
                        'navbar-vertical-default-link-active-color'
                      ),
                      left: 17
                    }}
                    onClick={() => setPreviewEmoji(!previewEmoji)}
                  >
                    <FontAwesomeIcon
                      icon={!previewEmoji ? ['far', 'laugh-beam'] : 'times'}
                    />
                  </Button>
                  {previewEmoji && (
                    <div
                      style={{
                        fontFamily: 'dana !important'
                      }}
                      dir="ltr"
                    >
                      <Picker
                        locale="fa"
                        set="google"
                        onEmojiSelect={e => {
                          let emoji = e.native;
                          const newValue = values.title + emoji;
                          setFieldValue('title', newValue);
                          setPreviewEmoji(false);
                        }}
                        theme={isDark ? 'dark' : 'light'}
                        previewPosition="none"
                        skinTonePosition="none"
                      />
                    </div>
                  )}

                  <Form.Control.Feedback
                    className="d-block"
                    type={errors.title ? 'invalid' : 'valid'}
                  >
                    {touched.title && errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
                <KeywordsForSelect
                  label="انتخاب کلمات کلیدی دسته"
                  placeholder="انتخاب کلمات کلیدی برای این دسته"
                  setFieldValue={setFieldValue}
                  values={values}
                  footer={
                    <div className="mt-2">
                      <span className="mx-1 text-success">
                        {trackerInfo?.data?.used?.num_of_groups}
                      </span>
                      دسته از
                      <span className="mx-1 text-info">
                        {trackerInfo?.data?.tariff?.num_of_groups}
                      </span>
                      دسته اضافه شده.
                    </div>
                  }
                />
                {/* دکمه های آخر صفحه */}
                <div className="position-sm-absolute bottom-0 d-flex gap-2 end-0 justify-content-end p-sm-4 pb-7 pt-2">
                  <Button
                    className="d-flex gap-2 align-items-center"
                    variant="outline-secondary"
                    onClick={() => dispatch(hideModal())}
                  >
                    <FontAwesomeIcon size="lg" icon="times" />
                    لغو
                  </Button>
                  <Button
                    className="d-flex gap-2 align-items-center border-0"
                    disabled={
                      trackerInfo?.data?.tariff?.num_of_groups -
                        trackerInfo?.data?.used?.num_of_groups <=
                      0
                    }
                    type="submit"
                    variant="falcon-primary"
                  >
                    <FontAwesomeIcon size="lg" icon="plus" />
                    اعمال کن
                  </Button>
                </div>
              </Form>
            )
          }
        </Formik>
      </Offcanvas.Body>

      <style>
        {/* استایل کلی دراور */}
        {`
          .offcanvas{
              width:500px !important;
                max-width:100% !important;
              }
            .offcanvas img {
              margin: 0 auto;
              max-width: 100%;
              height: auto;
              display: flex;
              }
 `}
      </style>
    </Offcanvas>
  );
};

export default AddGroupsDrawer;
